import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useCreateProject } from '@/hooks/useProjects'
import { projectSchema, type ProjectFormInput } from '@/lib/validations/project'
import { useUsage } from '@/hooks/useUsage'
import { UpgradeModal } from '@/components/usage/UpgradeModal'

interface CreateProjectDialogProps {
  children?: React.ReactNode
}

export function CreateProjectDialog({ children }: CreateProjectDialogProps) {
  const [open, setOpen] = useState(false)
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const createProject = useCreateProject()
  const { data: usage } = useUsage()

  const isBlocked = usage?.usage.projects.level === 'blocked'

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm<ProjectFormInput>({
    resolver: zodResolver(projectSchema),
  })

  const onSubmit = async (data: ProjectFormInput) => {
    try {
      await createProject.mutateAsync(data)
      toast.success('Project created successfully!')
      setOpen(false)
      reset()
    } catch (error: any) {
      toast.error(error.message || 'Failed to create project')
    }
  }

  // If user is at project limit, show upgrade modal instead
  const handleTriggerClick = () => {
    if (isBlocked) {
      setUpgradeOpen(true)
    }
  }

  return (
    <>
    <UpgradeModal open={upgradeOpen} onOpenChange={setUpgradeOpen} />
    <Dialog open={isBlocked ? false : open} onOpenChange={isBlocked ? undefined : setOpen}>
      <DialogTrigger asChild onClick={isBlocked ? handleTriggerClick : undefined}>
        {children || <Button>Create Project</Button>}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[525px]">
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
            <DialogDescription>
              Set up a new editorial project for your video production workflow.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">
                Project Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                placeholder="My Film Project"
                {...register('name')}
                aria-invalid={errors.name ? 'true' : 'false'}
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name.message}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Brief description of your project\u2026"
                rows={3}
                {...register('description')}
                aria-invalid={errors.description ? 'true' : 'false'}
              />
              {errors.description && (
                <p className="text-sm text-destructive">{errors.description.message}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="nle_type">NLE Type</Label>
              <Select
                onValueChange={(value) => setValue('nle_type', value as any)}
              >
                <SelectTrigger id="nle_type">
                  <SelectValue placeholder="Select NLE" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="premiere">Adobe Premiere</SelectItem>
                  <SelectItem value="avid">Avid Media Composer</SelectItem>
                  <SelectItem value="resolve">DaVinci Resolve</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createProject.isPending}>
              {createProject.isPending ? 'Creating...' : 'Create Project'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
    </>
  )
}
